import { Editor } from '@tiptap/react'
import React, { useCallback } from 'react'
import { ColorResult, RGBColor } from 'react-color'
import {
  getColorAsString,
  hexToRgba,
  isHexColor,
  isRgba,
  parseToObject,
} from 'common/utils/color-utils'
import NewColorPickerPopup from 'client/components/core/NewColorPickerPopup'
import Popover from 'client/components/core/Popover'
import { EditorExtensionEnum } from 'client/components/core/tiptap-editor/enums/editor-extensions-enum'
import { ToolbarButtonUi } from 'client/components/core/tiptap-editor/ui/toolbar-button-ui'
import { useToolbarCommands } from '../../hooks/use-toolbar-commands'
import { ColorPickerUi } from './ui/color-picker-ui'
import { ColorPreviewInnerUi } from './ui/color-preview-inner-ui'
import { ColorPreviewUi } from './ui/color-preview-ui'
import { PickerPopupUi } from './ui/picker-popup-ui'

interface InlineStyleControlsProps {
  editor: Editor
}

const DEFAULT_COLOR = 'rgba(0, 0, 0, 0)'

export const ColorPickerControls = ({ editor }: InlineStyleControlsProps) => {
  const { onSetTextColor } = useToolbarCommands(editor)

  const color: string = editor?.getAttributes('textStyle').color

  const getPrevColor = useCallback((): RGBColor => {
    let previousColor = color

    if (!isRgba(previousColor)) {
      previousColor = isHexColor(previousColor)
        ? hexToRgba(previousColor)
        : DEFAULT_COLOR
    }

    return parseToObject(previousColor)
  }, [color])

  const hasRgbChanged = useCallback(
    (color: RGBColor) => {
      const { r, g, b } = getPrevColor()
      return color.r !== r || color.g !== g || color.b !== b
    },
    [getPrevColor],
  )

  const handleChangeComplete = useCallback(
    (color: ColorResult) => {
      const rgb = { ...color.rgb }
      const prevA = getPrevColor().a

      if (hasRgbChanged(rgb) && prevA === 0) {
        rgb.a = 1
      }

      onSetTextColor(getColorAsString(rgb))
    },
    [onSetTextColor, getPrevColor, hasRgbChanged],
  )

  return (
    <ToolbarButtonUi active={editor.isActive(EditorExtensionEnum.Color)}>
      <ColorPickerUi>
        <Popover
          ignoredEventTypes={['focusout']}
          offsetValue={5}
          flipOptions={{
            fallbackAxisSideDirection: 'end',
            fallbackPlacements: ['top-start', 'right', 'bottom-start'],
          }}
          strategyValue={'fixed'}
          placement={'right-end'}
          triggerElement={
            <ColorPreviewUi>
              <ColorPreviewInnerUi color={color} />
            </ColorPreviewUi>
          }
          popoverContent={
            <PickerPopupUi>
              {/* @ts-expect-error */}
              <NewColorPickerPopup
                color={color}
                onChangeComplete={handleChangeComplete}
              />
            </PickerPopupUi>
          }
        />
      </ColorPickerUi>
    </ToolbarButtonUi>
  )
}
